// components/industry-templates/AutomotiveEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Car, Zap, Award, Users } from 'lucide-react';

export const AutomotiveEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Vehicle Showcase */}
      {industryData?.vehicle_showcase && (
        <Card className="border-0 bg-gradient-to-br from-red-500/20 to-orange-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Car className="w-8 h-8" style={{ color: theme.primary }} />
              Vehicle Showcase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.vehicle_showcase.map((vehicle: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border-2" style={{ borderColor: theme.primary }}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">{vehicle.model}</h3>
                      <p className="text-slate-300">{vehicle.manufacturer}</p>
                    </div>
                    {vehicle.launch && (
                      <Badge className="bg-yellow-500 text-black">NEW LAUNCH</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {vehicle.type && (
                      <div className="bg-white/5 rounded p-2 text-center">
                        <p className="text-xs text-slate-400">Type</p>
                        <p className="font-bold text-white text-sm">{vehicle.type}</p>
                      </div>
                    )}
                    {vehicle.fuel && (
                      <div className="bg-white/5 rounded p-2 text-center">
                        <p className="text-xs text-slate-400">Fuel</p>
                        <p className="font-bold text-white text-sm">{vehicle.fuel}</p>
                      </div>
                    )}
                    {vehicle.price_range && (
                      <div className="bg-white/5 rounded p-2 text-center">
                        <p className="text-xs text-slate-400">Price</p>
                        <p className="font-bold text-white text-sm">{vehicle.price_range}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Drives */}
      {industryData?.test_drives && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="w-6 h-6" style={{ color: theme.primary }} />
              Test Drive Experience
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white text-lg mb-4">Book your test drive with latest models</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                    {industryData.test_drives.models_available}
                  </p>
                  <p className="text-sm text-slate-400">Models Available</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                    {industryData.test_drives.tracks}
                  </p>
                  <p className="text-sm text-slate-400">Test Tracks</p>
                </div>
              </div>
              {industryData.test_drives.booking_required && (
                <p className="text-center text-slate-400 text-sm mt-4">
                  ⚠️ Pre-booking required
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manufacturers */}
      {industryData?.manufacturers && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Participating Manufacturers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {industryData.manufacturers.map((manufacturer: string, idx: number) => (
                <div key={idx} className="p-3 bg-white/5 rounded-lg text-center border border-white/10 hover:scale-105 transition-transform">
                  <p className="text-white font-medium text-sm">{manufacturer}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auto Show Features */}
      {industryData?.special_features && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Show Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {industryData.special_features.map((feature: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-1">{feature.name}</h4>
                  <p className="text-sm text-slate-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
